import * as React from "react";
import {
  Badge,
  Button,
  cn,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@finster/ui";
import {
  ClipboardCheck,
  Code,
  FileText,
  Image,
  PanelLeftClose,
  Palette,
  Search,
} from "lucide-react";
import type { HeaderLogo } from "../ThemeContext";
import type { Tab } from "../types";

const RECENT_CHAT_LIMIT = 5;

function chatRowLabel(tab: Tab): string {
  const firstUser = tab.session.messages.find((m) => m.role === "user");
  if (firstUser?.content?.trim()) {
    return firstUser.content.trim().replace(/\s+/g, " ");
  }
  return tab.session.name;
}

interface AppSideNavProps {
  headerLogo: HeaderLogo;
  sortedTabs: Tab[];
  activeTabId: string;
  onSelectTab: (tabId: string) => void;
  onAction: (actionId: string) => void;
}

const nodeEnv = process.env.NODE_ENV?.toLowerCase();
const showDevTools = !nodeEnv || nodeEnv === "development";

const primaryActions: { id: string; label: string; icon: React.ElementType; disabled?: boolean }[] = [
  { id: "proof-read", label: "Review mode", icon: ClipboardCheck, disabled: true },
  { id: "pdf-extractor", label: "PDF Extractor", icon: FileText, disabled: true },
];

const devActions: { id: string; label: string; icon: React.ElementType }[] = [
  { id: "slide-inspector", label: "Slide Inspector", icon: Code },
  { id: "design-inspector", label: "Design Inspector", icon: Palette },
  { id: "background-inspector", label: "Background Inspector", icon: Image },
];

export const AppSideNav: React.FC<AppSideNavProps> = ({
  headerLogo,
  sortedTabs,
  activeTabId,
  onSelectTab,
  onAction,
}) => {
  const { collapsed, setCollapsed } = useSidebar();
  const tabsWithMessages = sortedTabs.filter((tab) => tab.session.messages.length > 0);
  const recentTabs = [...tabsWithMessages]
    .sort((a, b) => b.session.updatedAt - a.session.updatedAt)
    .slice(0, RECENT_CHAT_LIMIT);
  const tabCount = tabsWithMessages.length;

  const logoBlock = (
    <img src={headerLogo.fullSrc} alt={headerLogo.alt} className="h-6 w-auto max-w-[140px]" />
  );

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 z-[60] bg-black/50 transition-opacity duration-300 ease-in-out",
          collapsed ? "pointer-events-none opacity-0" : "opacity-100"
        )}
        onClick={() => setCollapsed(true)}
        aria-hidden
      />
      <Sidebar
        className={cn(
          "!fixed inset-y-0 left-0 z-[70] !w-[300px] !bg-background shadow-xl transition-transform duration-300 ease-in-out",
          collapsed ? "!-translate-x-full" : "!translate-x-0"
        )}
      >
        <SidebarHeader className="flex-row items-center justify-between gap-1 px-4 py-3">
          <div className="min-w-0 flex-1 pl-1">{logoBlock}</div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                className="shrink-0 cursor-pointer rounded-full"
                onClick={() => setCollapsed(true)}
                aria-expanded={true}
                aria-label="Collapse sidebar"
              >
                <PanelLeftClose className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Collapse</TooltipContent>
          </Tooltip>
        </SidebarHeader>

        <SidebarContent className="px-4 py-4">
          <SidebarGroup className="!px-0">
            <SidebarMenu className="gap-2">
              {primaryActions.map(({ id, label, icon: Icon, disabled }) => {
                const actionButton = (
                  <Button
                    variant="outline"
                    shape="round"
                    className={cn(
                      "w-full",
                      disabled
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer hover:bg-accent"
                    )}
                    disabled={disabled}
                    onClick={() => {
                      if (disabled) return;
                      onAction(id);
                      setCollapsed(true);
                    }}
                  >
                    <Icon className={cn("h-4 w-4 shrink-0", disabled ? "text-muted-foreground" : "text-primary")} aria-hidden />
                    {label}
                  </Button>
                );

                return (
                  <SidebarMenuItem key={id}>
                    {disabled ? (
                      <span className="group/coming-soon relative block w-full cursor-not-allowed">
                        {actionButton}
                        <span className="pointer-events-none absolute bottom-full left-1/2 z-[90] mb-0.5 -translate-x-1/2 translate-y-2 whitespace-nowrap rounded-md bg-popover px-3 py-1.5 text-sm text-popover-foreground opacity-0 shadow-md transition-opacity group-hover/coming-soon:opacity-100">
                          Coming soon
                        </span>
                      </span>
                    ) : (
                      actionButton
                    )}
                  </SidebarMenuItem>
                );
              })}
              {showDevTools && devActions.map(({ id, label, icon: Icon }) => (
                <SidebarMenuItem key={id}>
                  <Button
                    variant="outline"
                    shape="round"
                    className="w-full cursor-pointer hover:bg-accent"
                    onClick={() => {
                      onAction(id);
                      setCollapsed(true);
                    }}
                  >
                    <Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                    {label}
                  </Button>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>

          <SidebarGroup className="min-h-0 flex-1 !px-0 pt-3">
            <SidebarGroupLabel className="mb-2 flex items-center justify-between gap-1 px-0.5 normal-case tracking-normal">
              <span className="text-[11px] font-medium text-muted-foreground">
                Recent Chats ({tabCount})
              </span>
              <div className="flex items-center gap-0.5">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className="h-8 w-8 cursor-pointer text-muted-foreground"
                      aria-label="Search chats"
                      onClick={() => {
                        onAction("search-chats");
                        setCollapsed(true);
                      }}
                    >
                      <Search className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Search chats</TooltipContent>
                </Tooltip>
                {/* <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className="h-8 w-8 text-primary"
                      onClick={canAddTab ? onAddTab : undefined}
                      disabled={!canAddTab}
                      aria-label={
                        canAddTab ? "New chat for selected slide" : "Maximum tabs reached"
                      }
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {canAddTab ? "New chat for selected slide" : "Maximum tabs reached"}
                  </TooltipContent>
                </Tooltip> */}
              </div>
            </SidebarGroupLabel>

            <SidebarMenu aria-label="Recent chats">
              {recentTabs.map((tab) => {
                const isActive = tab.id === activeTabId;
                const label = chatRowLabel(tab);
                const slideNumber = tab.session.slideNumber ?? tab.session.slideIndex + 1;
                return (
                  <SidebarMenuItem key={tab.id}>
                    <div className="group flex items-center gap-1 rounded-lg px-2 py-2 transition-colors hover:bg-accent/60">
                      <SidebarMenuButton
                        onClick={() => {
                          onSelectTab(tab.id);
                          setCollapsed(true);
                        }}
                        className="flex min-w-0 flex-1 items-center gap-2 rounded-none bg-transparent p-0 text-left hover:bg-transparent"
                      >
                        <span
                          className="min-w-0 flex-1 truncate text-[13px] text-foreground"
                          title={label}
                        >
                          {label}
                        </span>
                        <Badge
                          variant="default"
                          shape="round"
                          className="shrink-0 text-[10px] opacity-0 transition-opacity group-hover:opacity-100"
                        >
                          Slide {slideNumber}
                        </Badge>
                      </SidebarMenuButton>
                    </div>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>

            <div className="mt-2 flex justify-center px-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-[12px] text-muted-foreground"
                onClick={() => {
                  onAction("view-all-chats");
                  setCollapsed(true);
                }}
              >
                View All Chats
              </Button>
            </div>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
    </>
  );
};
